/* eslint-disable camelcase */
import fetch from '../../api/fetch';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import { ROLLOVER_DISPLACEMENT_TYPE_SECONDARY } from '../../utils/constants';
import { parseError } from '../../utils/fetchErrorParser';
import { modifyMetricData, modifyRolloverDisplacementResults } from '../../utils/getMetrics';
import store from '../store';
import { listHydraulics } from './hydraulics';
import { enqueueSnackbar } from './snackbar';

export const listRollover = (rollover) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_LOADING,
  });

  const body = {
    query: {
      wellsInfoId: rollover.wellsInfoId,
      runId: rollover.runId,
    },
  };

  return fetch
    .post('hydraulicsRollover/fetch', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then((data) => {
      const { currentUser } = store.getState();
      const currentUnit = currentUser?.preferences?.unitSystem;
      const { data: rolloverData = [], units = {} } = data || {};

      if (!Array.isArray(rolloverData) || rolloverData.length === 0) {
        dispatch({
          type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_SUCCESS,
          payload: {
            data: [],
            paginatedElements: 0,
            units,
          },
        });
        return;
      }

      const modifiedRolloverData = modifyMetricData(
        rolloverData,
        units,
        currentUnit
      );

      if (modifiedRolloverData?.[0]?.pumps_output !== undefined) {
        rolloverData[0].pumps_output = modifiedRolloverData[0].pumps_output;
      }

      if (modifiedRolloverData?.[0]?.pumps_spm !== undefined) {
        rolloverData[0].pumps_spm = modifiedRolloverData[0].pumps_spm;
      }

      if (!Array.isArray(rolloverData) || rolloverData.length === 0) {
        dispatch({
          type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_SUCCESS,
          payload: {
            data: [],
            paginatedElements: 0,
            units,
          },
        });
        return;
      }

      const fields = rolloverData[0]?.results?.[0] || {};
      const depthsSummary = fields?.depths_summary ?? {};

      const flatKeys = Object.keys(fields).filter(
        (key) => key.endsWith('_esd') || key.endsWith('_ecd')
      );

      const nestedKeys = Object.keys(depthsSummary).flatMap((depth) => [
      `${depth}_esd`,
      `${depth}_ecd`,
      ]);

      rolloverData[0].secondary_fields = [
        ...ROLLOVER_DISPLACEMENT_TYPE_SECONDARY,
      ];

      const esdEcdKeys = [...new Set([...flatKeys, ...nestedKeys])];

      const omitList = [
        'icp_esd',
        'icp_ecd',
        'lp_esd',
        'lp_ecd',
        'bh_esd',
        'bh_ecd',
      ];

      const filteredKeys = esdEcdKeys.filter(
        (key) => !omitList.includes(key)
      );

      const groupedFields = filteredKeys.reduce((acc, key) => {
        const prefix = key.replace(/_(esd|ecd)$/, '');
        if (!acc[prefix]) acc[prefix] = [];
        acc[prefix].push(key);
        return acc;
      }, {});

      const secondaryFieldsMap = new Map(
        rolloverData[0].secondary_fields.map(item => [
          item.label.toLowerCase(),
          item
        ])
      );

      Object.entries(groupedFields).forEach(([prefix, fieldArray]) => {
        const formattedLabel = prefix.replace(/_/g, ' ');
        const key = formattedLabel.toLowerCase();
        const existing = secondaryFieldsMap.get(key);

        secondaryFieldsMap.set(key, {
          label: existing?.label ?? formattedLabel,
          field: fieldArray,
        });
      });

      rolloverData[0].secondary_fields = Array.from(secondaryFieldsMap.values());

      if (
        units?.bh_ecd !== undefined &&
        Array.isArray(rolloverData[0]?.secondary_fields)
      ) {
        rolloverData[0].secondary_fields.forEach((sec) => {
          sec.field?.forEach((fKey) => {
            if (units[fKey] === undefined) {
              if (fKey.endsWith('_ecd')) units[fKey] = units.bh_ecd;
              if (fKey.endsWith('_esd')) units[fKey] = units.bh_esd;
            }
          });
        });
      }

      if (rolloverData[0]?.results) {
        const resultsData = modifyRolloverDisplacementResults(
          rolloverData[0].results,
          units,
          currentUnit
        );
        rolloverData[0].results = resultsData;
      }

      if (rolloverData[0]?.run_details_copy?.target_data) {
        const targetData = modifyMetricData(
          [rolloverData[0]?.run_details_copy?.target_data],
          units.target_data,
          currentUnit
        );
        rolloverData[0].run_details_copy.target_data = targetData[0];
      }

      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_SUCCESS,
        payload: {
          ...data,
          data: rolloverData,
          paginatedElements: rolloverData.length,
          units,
          ph_version: rolloverData[0]?.run_details_copy?.ph_version
        },
      });
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_ERROR,
        payload: { ...error, message: 'Error' },
      });
    });
};

export const createRollover = (body) => (dispatch) => {
  delete body.isNewRow;
  delete body._id;
  const input = {
    input: {
      wellsInfoId: body.wellsInfoId,
      runId: body.runId,
      fromFluidId: body.fromFluidId,
      toFluidId: body.toFluidId,
      pumps_output: body.pumps_output,
      pumps_spm: body.pumps_spm
    }
  };
  return fetch
    .post('hydraulicsRollover/create', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Rollover Created Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      const rollover = {
        runId: body.runId,
        wellsInfoId: body.wellsInfoId
      };
      dispatch(listRollover(rollover));
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      const rollover = {
        runId: body.runId,
        wellsInfoId: body.wellsInfoId
      };
      dispatch(listRollover(rollover));
    });
};
export const updateRollover = (row, body, hydraulicsBody) => (dispatch) => {
  const { query, isRolloverResults } = hydraulicsBody;
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_LOADING
  });
  const input = {
    query: {
      _id: row?._id
    },
    input: {
      wellsInfoId: body.wellsInfoId,
      runId: body.runId,
      fromFluidId: body.fromFluidId,
      toFluidId: body.toFluidId,
      pumps_output: body.pumps_output,
      pumps_spm: body?.pumps_spm
    }
  };
  return fetch
    .put('hydraulicsRollover', input)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Rollover Updated Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      const rollover = {
        runId: body.runId,
        wellsInfoId: body.wellsInfoId
      };
      dispatch(listRollover(rollover));
      if (isRolloverResults) {
        dispatch(listHydraulics(query));
      }
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      const rollover = {
        runId: body.runId,
        wellsInfoId: body.wellsInfoId
      };
      dispatch(listRollover(rollover));
      if (isRolloverResults) {
        dispatch(listHydraulics(query));
      }
    });
};
export const deleteRollover = (rollover) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_LOADING
  });
  const body = {
    query: {
      _id: rollover._id,
      wellsInfoId: rollover.wellsInfoId,
      runId: rollover.runId,
    }
  };
  return fetch
    .deleteAction('hydraulicsRollover', body)
    .then((result) => {
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Rollover deleted successfully',
          'success',
          new Date().getTime() + Math.random()
        ));
      dispatch(listRollover(rollover));
      if (rollover?.isRolloverResults) {
        dispatch(listHydraulics(rollover.query));
      }
    })
    .catch(async (error) => {
      const errorMessage = await parseError(error);
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listRollover(rollover));
      if (rollover?.isRolloverResults) {
        dispatch(listHydraulics(rollover.query));
      }
    });
};
export const runRolloverProxy = (body, disableLoader, onClose) => (dispatch) => {
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_LIST_LOADING,
  });
  dispatch({
    type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_LIST_LOADING,
  });
  const { runId, query } = body;
  const input = {
    input: {
      url: `/runs/rollover/${runId}`,
      method: 'POST',
    }
  };
  return fetch
    .post('premiumHydraulics/proxy', input)
    .then((result) => {
      disableLoader();
      if (!result.ok) {
        return Promise.reject(result);
      }
      return result.json();
    })
    .then(() => {
      dispatch(
        enqueueSnackbar(
          'Rollover Simulation Completed Successfully.',
          'success',
          new Date().getTime() + Math.random()
        ));
      onClose();
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_SET_FALSE,
      });
      dispatch(listHydraulics(query));
    })
    .catch(async (error) => {
      disableLoader();
      const errorMessage = await parseError(error);
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_ROLLOVER_SET_FALSE,
      });
      dispatch(
        enqueueSnackbar(
          errorMessage,
          'error',
          new Date().getTime() + Math.random()
        ));
      dispatch(listHydraulics(query));
    });
};
